'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { Button } from '@kit/ui/button';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from '@kit/ui/input-otp';
import { toast } from '@kit/ui/sonner';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { prepareSignupPhoneVerificationAction } from '../server/verify-signup-phone-actions';

/**
 * Skippable WhatsApp verification offered right after the email flows verify
 * the email (phone counterpart of SignupEmailVerifyStep). The signup core
 * stores a typed phone UNCONFIRMED; this step proves it while intent is
 * highest, using the same primitive as the profile Verify dialog:
 * signInWithOtp({ phone, shouldCreateUser: false }) + verifyOtp(type 'sms')
 * confirms the number as a side effect of verification.
 *
 * Self-skipping: the prepare action reports 'skip' when the session has no
 * phone or it's already confirmed (guest upgrade), and `onDone` fires
 * immediately — callers can render this step unconditionally after the email
 * verify. Skipping is always available (e.g. WhatsApp code never arrives):
 * the unverified phone is kept but suppressed from sends, and the profile
 * page offers verification later.
 */
export function SignupPhoneVerifyStep({
  tenantSlug,
  onDone,
}: {
  tenantSlug?: string;
  onDone: () => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const supabase = useSupabase();

  const [state, setState] = React.useState({
    phase: 'preparing' as 'preparing' | 'code',
    phone: '',
    code: '',
    verifying: false,
    resendCooldown: 0,
  });

  // One-shot guard for the on-mount prepare+send (Strict Mode double-invokes
  // effects; a ref keeps it to a single OTP).
  const startedRef = React.useRef(false);

  const sendCode = React.useCallback(
    async (phone: string) => {
      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { shouldCreateUser: false },
      });

      if (error) {
        toast.error(
          t(
            'auth:verifyPhoneSendFailed',
            "We couldn't send the code. Try again or skip for now.",
          ),
        );
        return false;
      }

      return true;
    },
    [supabase, t],
  );

  React.useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      try {
        const prepared = await prepareSignupPhoneVerificationAction({
          tenantSlug:
            tenantSlug || process.env.NEXT_PUBLIC_DEFAULT_LANDING_PAGE,
        });

        if (prepared.status !== 'ready') {
          // No phone / already confirmed / lookup failed — never block the
          // signup on this optional step.
          await onDone();
          return;
        }

        setState((s) => ({
          ...s,
          phase: 'code',
          phone: prepared.phone,
          resendCooldown: 30,
        }));

        const sent = await sendCode(prepared.phone);

        if (!sent) {
          // The "try again" the failure toast advertises must actually be
          // available — don't leave a 30s cooldown armed for a send that
          // never happened.
          setState((s) => ({ ...s, resendCooldown: 0 }));
        }
      } catch {
        await onDone();
      }
    })();
    // One-shot on mount by design (startedRef); deps kept for correctness.
  }, [onDone, sendCode, tenantSlug]);

  React.useEffect(() => {
    if (state.resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setState((s) => ({ ...s, resendCooldown: s.resendCooldown - 1 }));
    }, 1000);

    return () => clearInterval(timer);
  }, [state.resendCooldown]);

  const handleVerify = async () => {
    setState((s) => ({ ...s, verifying: true }));

    try {
      const { error } = await supabase.auth.verifyOtp({
        phone: state.phone,
        token: state.code,
        type: 'sms',
      });

      if (error) {
        toast.error(
          t('auth:verifyPhoneInvalidCode', "That code didn't work. Try again."),
        );
        setState((s) => ({ ...s, code: '', verifying: false }));
        return;
      }

      toast.success(
        t('auth:verifyPhoneSuccess', 'WhatsApp number verified — you’re all set.'),
      );
      await onDone();
    } catch {
      toast.error(t('auth:errors.generic'));
      setState((s) => ({ ...s, verifying: false }));
    }
  };

  const handleResend = async () => {
    setState((s) => ({ ...s, resendCooldown: 30, code: '' }));

    const sent = await sendCode(state.phone);

    if (!sent) {
      setState((s) => ({ ...s, resendCooldown: 0 }));
    }
  };

  if (state.phase === 'preparing') {
    return (
      <div className="flex w-full justify-center py-10">
        <Spinner className="h-5 w-5" />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col items-center gap-y-6">
      <div className="text-center">
        <p className="text-base font-medium">
          <Trans
            i18nKey="auth:verifyPhoneStepTitle"
            defaults="Verify your WhatsApp number"
          />
        </p>

        <p className="text-muted-foreground mt-1 text-sm">
          <Trans
            i18nKey="auth:verifyPhoneStepSubtitle"
            values={{ phone: state.phone }}
            defaults="We sent a 6-digit code to {{phone}} on WhatsApp. Enter it below, or skip and verify later from your profile."
          />
        </p>
      </div>

      <InputOTP
        maxLength={6}
        value={state.code}
        onChange={(code) => setState((s) => ({ ...s, code }))}
        disabled={state.verifying}
      >
        <InputOTPGroup>
          <InputOTPSlot index={0} />
          <InputOTPSlot index={1} />
          <InputOTPSlot index={2} />
        </InputOTPGroup>
        <InputOTPSeparator />
        <InputOTPGroup>
          <InputOTPSlot index={3} />
          <InputOTPSlot index={4} />
          <InputOTPSlot index={5} />
        </InputOTPGroup>
      </InputOTP>

      <div className="flex w-full flex-col gap-y-2">
        <Button
          type="button"
          disabled={state.code.length !== 6 || state.verifying}
          onClick={handleVerify}
          data-test="signup-phone-verify-button"
        >
          {state.verifying ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              <Trans i18nKey="auth:verifyingCode" />
            </>
          ) : (
            <Trans
              i18nKey="auth:verifyPhoneButton"
              defaults="Verify WhatsApp number"
            />
          )}
        </Button>

        <Button
          type="button"
          variant="ghost"
          disabled={state.verifying || state.resendCooldown > 0}
          onClick={handleResend}
        >
          {state.resendCooldown > 0 ? (
            <Trans
              i18nKey="auth:resendCodeIn"
              values={{ seconds: state.resendCooldown }}
            />
          ) : (
            <Trans i18nKey="auth:requestNewCode" />
          )}
        </Button>

        <Button
          type="button"
          variant="link"
          className="text-muted-foreground"
          disabled={state.verifying}
          onClick={() => void onDone()}
          data-test="signup-phone-verify-skip-button"
        >
          <Trans i18nKey="auth:verifyEmailSkip" defaults="Skip for now" />
        </Button>
      </div>
    </div>
  );
}
